/**
 * Registro da entrega de cópia do prontuário, com recibo assinado por quem
 * recebeu.
 *
 * O recibo responde o que costuma ser contestado depois: o que saiu (período,
 * conteúdo, páginas, formato), para quem (paciente ou terceiro legitimado) e
 * quem da clínica entregou. Assinado o recibo, o PDF fica na aba Contratos.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/ReceiptLongRounded';

import DeliveryMethodPicker from './DeliveryMethodPicker';
import CollectSignaturesDialog from './CollectSignaturesDialog';
import { createMedicalRecordDelivery } from '../../services/signatureService';
import {
  MEDICAL_RECORD_CONTENT_LABELS,
  MEDICAL_RECORD_FORMAT_LABELS,
  MEDICAL_RECORD_RECIPIENT_LABELS,
} from '../../constants/signature';
import { maskCpf, maskPhone, onlyDigits } from '../../utils/signatureInputMasks';
import type {
  DeliveryMethod,
  EnvelopeDetail,
  MedicalRecordContentType,
  MedicalRecordDelivery,
  MedicalRecordDeliveryFormat,
  MedicalRecordRecipientType,
} from '../../types/signature';

export interface MedicalRecordDeliveryPatient {
  id: string;
  full_name: string;
  cpf?: string | null;
  phone?: string | null;
}

interface MedicalRecordDeliveryDialogProps {
  open: boolean;
  onClose: () => void;
  patient: MedicalRecordDeliveryPatient;
  /** Pré-seleção vinda de quem abriu (ex.: exportação de evoluções). */
  defaultContentTypes?: MedicalRecordContentType[];
  onCreated?: (delivery: MedicalRecordDelivery) => void;
}

const RECIPIENT_TYPES = Object.keys(MEDICAL_RECORD_RECIPIENT_LABELS) as MedicalRecordRecipientType[];
const CONTENT_TYPES = Object.keys(MEDICAL_RECORD_CONTENT_LABELS) as MedicalRecordContentType[];
const FORMATS = Object.keys(MEDICAL_RECORD_FORMAT_LABELS) as MedicalRecordDeliveryFormat[];

const RELATIONSHIP_PLACEHOLDER: Record<MedicalRecordRecipientType, string> = {
  patient: '',
  legal_guardian: 'Ex.: mãe, pai, curador(a)',
  attorney: 'Ex.: advogado(a)',
  authorized_third_party: 'Ex.: cônjuge, filho(a)',
};

const DOCUMENT_PLACEHOLDER: Record<MedicalRecordRecipientType, string> = {
  patient: '',
  legal_guardian: 'Ex.: certidão de nascimento, termo de curatela',
  attorney: 'Ex.: procuração com poderes específicos, OAB/UF 000000',
  authorized_third_party: 'Ex.: autorização por escrito do paciente',
};

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function MedicalRecordDeliveryDialog({
  open,
  onClose,
  patient,
  defaultContentTypes,
  onCreated,
}: MedicalRecordDeliveryDialogProps) {
  const [recipientType, setRecipientType] = useState<MedicalRecordRecipientType>('patient');
  const [recipientName, setRecipientName] = useState('');
  const [recipientCpf, setRecipientCpf] = useState('');
  const [recipientRelationship, setRecipientRelationship] = useState('');
  const [recipientDocument, setRecipientDocument] = useState('');
  const [phone, setPhone] = useState('');

  const [fullRecord, setFullRecord] = useState(true);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [contents, setContents] = useState<MedicalRecordContentType[]>([]);
  const [format, setFormat] = useState<MedicalRecordDeliveryFormat>('printed');
  const [digitalChannel, setDigitalChannel] = useState('');
  const [pageCount, setPageCount] = useState('');
  const [requestedAt, setRequestedAt] = useState(today());
  const [notes, setNotes] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('device');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collect, setCollect] = useState<EnvelopeDetail | null>(null);
  /** Envio por WhatsApp falhou: a entrega existe e o recibo pode ser assinado aqui. */
  const [fallbackEnvelope, setFallbackEnvelope] = useState<EnvelopeDetail | null>(null);
  const defaultContentKey = (defaultContentTypes || []).join(',');

  useEffect(() => {
    if (!open) return;
    setRecipientType('patient');
    setRecipientName('');
    setRecipientCpf('');
    setRecipientRelationship('');
    setRecipientDocument('');
    setPhone(maskPhone(patient.phone));
    setFullRecord(true);
    setPeriodStart('');
    setPeriodEnd('');
    setContents(
      defaultContentKey ? (defaultContentKey.split(',') as MedicalRecordContentType[]) : [],
    );
    setFormat('printed');
    setDigitalChannel('');
    setPageCount('');
    setRequestedAt(today());
    setNotes('');
    setDeliveryMethod('device');
    setError(null);
    setCollect(null);
    setFallbackEnvelope(null);
  }, [open, patient.phone, defaultContentKey]);

  const isPatient = recipientType === 'patient';

  const changeRecipientType = (next: MedicalRecordRecipientType) => {
    setRecipientType(next);
    setPhone(next === 'patient' ? maskPhone(patient.phone) : '');
  };

  const toggleContent = (key: MedicalRecordContentType) =>
    setContents((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const validationError = useMemo(() => {
    if (!isPatient) {
      if (!recipientName.trim()) return 'Informe o nome de quem está recebendo.';
      const cpf = onlyDigits(recipientCpf);
      if (cpf && cpf.length !== 11) return 'CPF de quem recebe está incompleto.';
      if (!cpf && !recipientDocument.trim()) {
        return 'Para terceiro, informe o CPF ou o documento que comprova a legitimidade.';
      }
    }
    if (!fullRecord) {
      if (!periodStart && !periodEnd) return 'Informe o período entregue ou marque prontuário completo.';
      if (periodStart && periodEnd && periodStart > periodEnd) return 'O período começa depois de terminar.';
    }
    if (deliveryMethod === 'whatsapp' && onlyDigits(phone).length < 10) {
      return 'Informe o WhatsApp para enviar o link do recibo.';
    }
    return null;
  }, [isPatient, recipientName, recipientCpf, recipientDocument, fullRecord, periodStart, periodEnd, deliveryMethod, phone]);

  const handleSubmit = async () => {
    if (fallbackEnvelope) {
      setCollect(fallbackEnvelope);
      return;
    }
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await createMedicalRecordDelivery({
        patient_id: patient.id,
        recipient_type: recipientType,
        recipient_name: isPatient ? undefined : recipientName.trim(),
        recipient_cpf: isPatient ? undefined : onlyDigits(recipientCpf) || undefined,
        recipient_phone: onlyDigits(phone) || undefined,
        recipient_document: isPatient ? undefined : recipientDocument.trim() || undefined,
        recipient_relationship: isPatient ? undefined : recipientRelationship.trim() || undefined,
        delivery_format: format,
        digital_channel: format === 'printed' ? undefined : digitalChannel.trim() || undefined,
        period_start: fullRecord ? null : periodStart || null,
        period_end: fullRecord ? null : periodEnd || null,
        content_types: contents,
        page_count: pageCount ? Number(pageCount) : null,
        requested_at: requestedAt || null,
        notes: notes.trim() || null,
        delivery_method: deliveryMethod,
      });
      onCreated?.(result.delivery);

      const failed = result.envelope?.delivery?.find((d) => !d.sent);
      if (deliveryMethod === 'device') {
        setCollect(result.envelope);
      } else if (failed) {
        setError(
          `Entrega registrada, mas o WhatsApp não foi enviado: ${failed.reason || 'falha no envio'}. ` +
            'Colete a assinatura aqui na clínica.',
        );
        setFallbackEnvelope(result.envelope);
      } else {
        onClose();
      }
    } catch (e: any) {
      setError(e?.message || 'Não foi possível registrar a entrega.');
    } finally {
      setSaving(false);
    }
  };

  if (collect) {
    return (
      <CollectSignaturesDialog
        open={open}
        onClose={() => {
          setCollect(null);
          onClose();
        }}
        envelopeId={collect.envelope.id}
        signers={collect.signers}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ReceiptIcon sx={{ color: '#0D9488' }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Entrega de prontuário</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              {patient.full_name} · o recibo é assinado por quem recebe a cópia
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.disabled' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Quem está recebendo</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={recipientType}
              onChange={(_, v) => v && changeRecipientType(v)}
              sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
            >
              {RECIPIENT_TYPES.map((type) => (
                <ToggleButton key={type} value={type}>
                  {MEDICAL_RECORD_RECIPIENT_LABELS[type]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {isPatient ? (
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>
                O recibo sai em nome de {patient.full_name}
                {patient.cpf ? `, CPF ${maskCpf(patient.cpf)}` : ''}.
              </Typography>
            ) : (
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Nome completo"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="CPF"
                    value={recipientCpf}
                    onChange={(e) => setRecipientCpf(maskCpf(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Vínculo com o paciente"
                    placeholder={RELATIONSHIP_PLACEHOLDER[recipientType]}
                    value={recipientRelationship}
                    onChange={(e) => setRecipientRelationship(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Documento que comprova a legitimidade"
                    placeholder={DOCUMENT_PLACEHOLDER[recipientType]}
                    value={recipientDocument}
                    onChange={(e) => setRecipientDocument(e.target.value)}
                    helperText="Confira o documento original antes de entregar."
                  />
                </Grid>
              </Grid>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>O que está sendo entregue</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={fullRecord ? 'full' : 'period'}
                onChange={(_, v) => v && setFullRecord(v === 'full')}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
              >
                <ToggleButton value="full">Prontuário completo</ToggleButton>
                <ToggleButton value="period">Período</ToggleButton>
              </ToggleButtonGroup>
              {!fullRecord && (
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    type="date"
                    label="De"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Até"
                    value={periodEnd}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              )}
            </Stack>

            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.75 }}>
              Conteúdo (deixe em branco para registrar como prontuário integral)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {CONTENT_TYPES.map((key) => {
                const selected = contents.includes(key);
                return (
                  <Chip
                    key={key}
                    label={MEDICAL_RECORD_CONTENT_LABELS[key]}
                    size="small"
                    onClick={() => toggleContent(key)}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 600,
                      ...(selected
                        ? { bgcolor: '#0D9488', color: '#fff', '&:hover': { bgcolor: '#0f766e' } }
                        : { borderColor: '#99f6e4', color: '#0f766e' }),
                    }}
                  />
                );
              })}
            </Box>

            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Formato"
                  value={format}
                  onChange={(e) => setFormat(e.target.value as MedicalRecordDeliveryFormat)}
                >
                  {FORMATS.map((f) => (
                    <MenuItem key={f} value={f}>
                      {MEDICAL_RECORD_FORMAT_LABELS[f]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {format !== 'printed' && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Meio digital"
                    placeholder="Ex.: e-mail, pen drive"
                    value={digitalChannel}
                    onChange={(e) => setDigitalChannel(e.target.value)}
                  />
                </Grid>
              )}
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Páginas"
                  value={pageCount}
                  onChange={(e) => setPageCount(e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6} sm={format !== 'printed' ? 2 : 6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Solicitado em"
                  value={requestedAt}
                  onChange={(e) => setRequestedAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Observações"
                  placeholder="Ex.: exames de imagem entregues em CD; laudo de 12/03 pendente"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Assinatura do recibo</Typography>
            <DeliveryMethodPicker value={deliveryMethod} onChange={setDeliveryMethod} />
            {deliveryMethod === 'whatsapp' && (
              <TextField
                size="small"
                label="WhatsApp de quem recebe"
                value={phone}
                onChange={(e) => setPhone(maskPhone(e.target.value))}
                sx={{ mt: 1.5, maxWidth: 260 }}
                fullWidth
              />
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            bgcolor: '#0D9488',
            '&:hover': { bgcolor: '#0f766e' },
          }}
        >
          {fallbackEnvelope
            ? 'Coletar assinatura aqui'
            : deliveryMethod === 'whatsapp'
              ? 'Registrar e enviar recibo'
              : 'Registrar e coletar assinatura'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
